import { useQuery } from '@apollo/client/react';
import { DimeButton, DimeCardShellSkeleton, DimeDetailPageStates } from '@nudool/design-system';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { CalendarXIcon } from 'lucide-react';

import { useBreadcrumbLabel } from '../../components/BreadcrumbLabelsContext.js';
import { assertUuidRouteParams } from '../../lib/assert-uuid-route-params.js';
import { isNotFoundGraphQLError } from '../../lib/is-not-found-graphql-error.js';
import { RouteGuard } from '../../lib/route-guard.js';
import {
  GET_CAL_EVENT,
  type GetCalEventQueryResult,
  type GetCalEventQueryVariables,
} from '../../modules/events/detail/event-detail-operations.js';
import { EventDetailView } from '../../modules/events/detail/event-detail-view.js';

export const Route = createFileRoute('/events/$eventId')({
  beforeLoad: ({ params }) => assertUuidRouteParams(params),
  validateSearch: (search: Record<string, unknown>) => ({
    edit: search.edit === true || search.edit === 'true',
  }),
  component: EventDetailRoute,
});

// eslint-disable-next-line react-refresh/only-export-components -- the route file exports Route and registers its page component by reference.
function EventDetailPage() {
  const navigate = useNavigate();
  const { eventId } = Route.useParams();
  const { edit: startEditing } = Route.useSearch();
  const { data, loading, error, refetch } = useQuery<
    GetCalEventQueryResult,
    GetCalEventQueryVariables
  >(GET_CAL_EVENT, { variables: { id: eventId } });
  useBreadcrumbLabel(eventId, data?.calEvent?.name ?? undefined);

  return (
    <DimeDetailPageStates
      data={data?.calEvent}
      loading={loading}
      error={isNotFoundGraphQLError(error, 'calEvent') ? null : error}
      entityLabel="Event"
      notFoundIcon={<CalendarXIcon />}
      notFoundAction={
        <DimeButton variant="outline" asChild>
          <Link to="/events">Back to events</Link>
        </DimeButton>
      }
      skeleton={<DimeCardShellSkeleton factCount={2} actionCount={2} tabCount={2} rowCount={4} />}
      onRetry={() => void refetch().catch(() => undefined)}
    >
      {(event) => (
        <EventDetailView
          event={event}
          initialEditing={startEditing}
          onEditingChange={(editing) =>
            void navigate({
              to: '/events/$eventId',
              params: { eventId },
              search: { edit: editing },
              replace: true,
            })
          }
        />
      )}
    </DimeDetailPageStates>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- the route file exports Route and registers its page component by reference.
function EventDetailRoute() {
  return (
    <RouteGuard route="/events/$eventId">
      <EventDetailPage />
    </RouteGuard>
  );
}
